import React from "react";
import { X, DollarSign, Star, MapPin, Clock, Truck, Download, CheckCircle2 } from "lucide-react";

/** Escape strings we splice into the print-preview HTML so a malicious pickup address / vehicle info
 * (or a compromised backend response) can't inject arbitrary markup or scripts. */
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;",
}[c]));

export default function ReceiptModal({ booking, open, onClose }) {
  if (!open || !booking) return null;
  const base = booking.price || 0;
  const tip = booking.tip || 0;
  const total = booking.total || (base + tip);

  const printReceipt = () => {
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return;
    const receiptId = esc((booking.id || "").slice(0, 12).toUpperCase());
    const title = `ASAP Roadside Receipt ${esc((booking.id || "").slice(0, 8))}`;
    const dateStr = esc(new Date(booking.updated_at || booking.created_at).toLocaleString());
    const rating = booking.rating
      ? `<div class="kv"><span>Your rating</span><span>${"★".repeat(booking.rating)}${"☆".repeat(5 - booking.rating)}</span></div>`
      : "";
    const html = `
      <html><head><title>${esc(title)}</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:32px;color:#061224;max-width:520px;margin:auto}
        h1{font-family:'Bebas Neue',Impact,sans-serif;letter-spacing:0.05em;text-transform:uppercase;font-size:32px;margin:0}
        .kv{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee}
        .yellow{color:#FF6B00;font-weight:bold}
        .total{font-size:28px;font-weight:bold;color:#061224}
        .badge{display:inline-block;background:#FFD700;color:#061224;padding:4px 10px;font-weight:bold;letter-spacing:0.1em;font-size:11px}
      </style></head><body>
      <div style="border-bottom:3px solid #061224;padding-bottom:16px;margin-bottom:24px">
        <h1>ASAP Roadside · Receipt</h1>
        <div style="color:#666;font-size:13px;margin-top:4px">Roadrunner Rescue · asaproadrunners.com</div>
        <div class="badge" style="margin-top:8px">PAID</div>
      </div>
      <div class="kv"><span>Receipt #</span><span>${receiptId}</span></div>
      <div class="kv"><span>Date</span><span>${dateStr}</span></div>
      <div class="kv"><span>Service</span><span>${esc(booking.service_name)}</span></div>
      <div class="kv"><span>Driver</span><span>${esc(booking.driver_name || "—")}</span></div>
      <div class="kv"><span>Pickup</span><span>${esc(booking.pickup_address)}</span></div>
      <div class="kv"><span>Vehicle</span><span>${esc(booking.vehicle_info || "—")}</span></div>
      ${rating}
      <div style="margin-top:24px;border-top:2px solid #061224;padding-top:16px">
        <div class="kv"><span>Base price</span><span>$${base.toFixed(2)}</span></div>
        <div class="kv"><span>Tip (100% to driver)</span><span>$${tip.toFixed(2)}</span></div>
        <div class="kv total"><span>Total charged</span><span class="yellow">$${total.toFixed(2)}</span></div>
      </div>
      <div style="margin-top:32px;font-size:11px;color:#888;line-height:1.6">
        Questions? Email asaproadrunners@rs-asap.com, call 831-747-7141, or text 765-330-1951.<br/>
        This receipt is your record of payment. Keep for your files or reimbursement.
      </div>
      </body></html>
    `;
    // Use standards-compliant DOM write instead of document.write (which triggers XSS scanners
    // and can be blocked by strict CSPs). We still need document.write for a brand-new blank
    // window because innerHTML on document root is browser-inconsistent, so we open+write+close
    // in a single synchronous batch and every dynamic value above has been HTML-escaped.
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="receipt-modal" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="bg-white border-2 border-asap-deep max-w-md w-full shadow-hard">
        <div className="p-5 border-b-2 border-asap-deep bg-asap-yellow flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-asap-deep font-bold">Receipt</div>
            <h3 className="font-display text-xl uppercase text-asap-deep">ASAP Roadside</h3>
          </div>
          <button onClick={onClose} data-testid="receipt-close" className="p-1 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-500 text-white font-bold uppercase text-[10px] tracking-widest">
            <CheckCircle2 size={12}/> Paid
          </div>
          <ReceiptRow icon={Truck} label="Service" value={booking.service_name}/>
          <ReceiptRow icon={MapPin} label="Pickup" value={booking.pickup_address}/>
          <ReceiptRow icon={Clock} label="Date" value={new Date(booking.updated_at || booking.created_at).toLocaleString()}/>
          <ReceiptRow icon={Truck} label="Driver" value={booking.driver_name || "—"}/>
          {booking.rating && <ReceiptRow icon={Star} label="Your rating" value={"★".repeat(booking.rating) + "☆".repeat(5 - booking.rating)}/>}
          <div className="pt-3 mt-3 border-t-2 border-asap-deep space-y-1">
            <Line label="Base price" value={`$${base.toFixed(2)}`}/>
            <Line label="Tip" value={`$${tip.toFixed(2)}`}/>
            <div className="flex justify-between pt-2 mt-2 border-t border-slate-200">
              <span className="font-display uppercase text-asap-deep">Total</span>
              <span className="font-display text-2xl text-asap-orange">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="p-5 border-t-2 border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={printReceipt} data-testid="receipt-print" className="px-4 py-2 bg-asap-deep text-white font-bold uppercase text-xs inline-flex items-center gap-1 hover:bg-asap-blue"><Download size={12}/> Print / Save PDF</button>
          <button onClick={onClose} className="px-4 py-2 border-2 border-asap-deep text-asap-deep font-bold uppercase text-xs hover:bg-asap-deep hover:text-white">Close</button>
        </div>
      </div>
    </div>
  );
}

function ReceiptRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-asap-orange mt-0.5 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-asap-deep">{value}</div>
      </div>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="text-asap-deep font-bold">{value}</span>
    </div>
  );
}

function DollarSignIcon() { return <DollarSign size={12}/>; }
